import { getCdnOrigin } from '@/lib/cdnBase';
import { supabase } from '../supabase.client';

/** 공지 이미지 업로드가 쓰는 R2 prefix. EF(rb-upload-image)의 ALLOWED_PREFIXES 와 일치해야 함. */
export type R2UploadPrefix = 'rb-notices';

/**
 * 공지 이미지를 우리 R2 에 업로드하고 공개 CDN URL 을 반환한다.
 * rb-upload-image EF 가 ADMIN_EMAILS 게이트를 통과한 호출에 한해 서버사이드로 R2 에
 * PUT 하고 키를 돌려주며, 공개 URL 은 여기서 `<VITE_R2_PUBLIC_BASE>/<key>` 로 조립한다.
 * (V8 CDN 과 달리 토큰/verse/카테고리 화이트리스트 제약이 없다.)
 */
export async function uploadImageToR2(file: File, prefix: R2UploadPrefix): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  form.append('prefix', prefix);

  const { data, error } = await supabase.functions.invoke('rb-upload-image', { body: form });
  if (error) {
    // FunctionsHttpError 의 실제 본문(예: "file too large", "prefix not allowed")은
    // error.context(Response)에 있다 — 제네릭 "non-2xx" 대신 진짜 사유를 노출.
    const detail = await readEdgeErrorDetail(error);
    throw new Error(`이미지 업로드 실패: ${detail}`);
  }
  const key = (data as { key?: unknown } | null)?.key;
  if (typeof key !== 'string' || !key) throw new Error('업로드 응답에 key 가 없습니다.');
  return `${getCdnOrigin()}/${key}`;
}

/** supabase-js 업로드 에러에서 EF 응답 본문을 추출(없으면 메시지로 폴백). */
async function readEdgeErrorDetail(error: { message: string; context?: unknown }): Promise<string> {
  const ctx = error.context;
  if (ctx instanceof Response) {
    const body = await ctx.text().catch(() => '');
    if (body) return body;
  }
  return error.message;
}

/**
 * vx-upload-image Edge Function 프록시를 통해 상품 이미지를 업로드하고 공개 CDN
 * URL 을 반환한다. 브라우저는 create.verse8.io/api/upload 를 직접 호출할 수 없어
 * (CORS 없음) EF 가 V8 토큰으로 서버사이드 대행한다.
 */
export async function uploadProductImage(
  file: File,
  v8Token: string,
  category = 'vx-shop'
): Promise<string> {
  if (!v8Token) {
    throw new Error('V8 토큰이 없습니다 — 어드민 로그인 토큰을 먼저 갱신하세요.');
  }
  const form = new FormData();
  form.append('file', file);
  form.append('v8Token', v8Token);
  form.append('category', category);

  // callEdgeFunction(JSON 전용) 대신 invoke 직접 호출 — multipart FormData 본문 +
  // 업로드 전용 에러 메시지 + url 누락 체크가 필요하기 때문.
  const { data, error } = await supabase.functions.invoke('vx-upload-image', { body: form });
  if (error) throw new Error(`이미지 업로드 실패: ${error.message}`);
  const url = (data as { url?: unknown } | null)?.url;
  if (typeof url !== 'string' || !url) throw new Error('업로드 응답에 url 이 없습니다.');
  return url;
}
